import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import api, { inr, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const ZONES = ["A", "B", "C", "D", "E"];
const inputCls = "w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:border-shipany-blue focus:outline-none";

export default function AdminRateCards() {
  const [cards, setCards] = useState(null);
  const [couriers, setCouriers] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [provFilter, setProvFilter] = useState("");
  const [form, setForm] = useState({ courier_code: "", zone: "A", base_rate: "", additional_rate: "", cod_fee: "", cod_pct: "1.8", fuel_surcharge_pct: "10", other_charges: "8" });

  const load = useCallback(() => {
    api.get("/admin/rate-cards").then(({ data }) => setCards(data)).catch(() => {});
    api.get("/admin/couriers").then(({ data }) => setCouriers(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const provCounts = useMemo(() => {
    const m = {};
    (cards || []).filter((c) => c.active).forEach((c) => { m[c.provider_code] = (m[c.provider_code] || 0) + 1; });
    return m;
  }, [cards]);

  const active = useMemo(() => (cards || []).filter((c) => c.active && (!provFilter || c.provider_code === provFilter) && (!filter || c.courier_code === filter)), [cards, filter, provFilter]);

  const openNew = (courier_code = "", zone = "A") => {
    setForm({ courier_code, zone, base_rate: "", additional_rate: "", cod_fee: "", cod_pct: "1.8", fuel_surcharge_pct: "10", other_charges: "8" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/rate-cards", {
        ...form,
        base_rate: Number(form.base_rate), additional_rate: Number(form.additional_rate),
        cod_fee: Number(form.cod_fee), cod_pct: Number(form.cod_pct),
        fuel_surcharge_pct: Number(form.fuel_surcharge_pct), other_charges: Number(form.other_charges),
      });
      toast.success("New rate card version saved (previous version archived)");
      setOpen(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-rate-cards-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Rate Cards</h1>
          <p className="text-sm text-slate-500">Versioned zone/weight pricing per courier. CSV import arrives in Phase 2.</p>
        </div>
        <button onClick={() => openNew()} data-testid="rate-card-new-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">
          <Plus className="h-4 w-4" /> New Rate Version
        </button>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="rate-card-provider-tabs">
        {[["", "All providers"], ["shipmozo", "Shipmozo"], ["shiprocket", "Shiprocket"], ["icarry", "iCarry"]].map(([v, label]) => (
          <button key={v} onClick={() => setProvFilter(v)} data-testid={`rc-tab-${v || "all"}`}
            className={`rounded-lg px-4 py-2 text-xs font-bold ${provFilter === v ? "bg-shipany-blue text-white" : "bg-white text-slate-500 border border-slate-200"}`}>
            {label} ({v ? provCounts[v] || 0 : Object.values(provCounts).reduce((a, b) => a + b, 0)})
          </button>
        ))}
      </div>

      <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="rate-card-filter"
        className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none">
        <option value="">All couriers</option>
        {couriers.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
      </select>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="rate-cards-table">
        <table className="w-full min-w-[960px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Courier</th><th className="px-4 py-3.5">Zone</th><th className="px-4 py-3.5">Ver</th>
              <th className="px-4 py-3.5">Base (0.5kg)</th><th className="px-4 py-3.5">+0.5kg</th><th className="px-4 py-3.5">COD Fee</th>
              <th className="px-4 py-3.5">COD %</th><th className="px-4 py-3.5">Fuel %</th><th className="px-4 py-3.5">Other</th><th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {active.map((rc) => (
              <tr key={rc.rate_card_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`rate-card-${rc.courier_code}-${rc.zone}`}>
                <td className="px-5 py-3"><p className="font-semibold text-shipany-navy">{rc.courier_name}</p><p className="mono text-[10px] capitalize text-slate-400">{rc.provider_code}</p></td>
                <td className="px-4 py-3"><span className="rounded bg-shipany-blue/10 px-2 py-0.5 text-xs font-bold text-shipany-blue">{rc.zone}</span></td>
                <td className="mono px-4 py-3 text-slate-500">v{rc.version}</td>
                <td className="mono px-4 py-3 font-semibold">{inr(rc.base_rate)}</td>
                <td className="mono px-4 py-3">{inr(rc.additional_rate)}</td>
                <td className="mono px-4 py-3">{inr(rc.cod_fee)}</td>
                <td className="mono px-4 py-3">{rc.cod_pct}%</td>
                <td className="mono px-4 py-3">{rc.fuel_surcharge_pct}%</td>
                <td className="mono px-4 py-3">{inr(rc.other_charges)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => openNew(rc.courier_code, rc.zone)} data-testid={`rate-card-revise-${rc.courier_code}-${rc.zone}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">Revise</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="rate-card-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <form onSubmit={save} className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">New Rate Version</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3.5">
              <div><label className="text-xs font-semibold text-slate-500">Courier</label>
                <select data-testid="rc-form-courier" className={`${inputCls} mt-1`} required value={form.courier_code} onChange={(e) => setForm({ ...form, courier_code: e.target.value })}>
                  <option value="">Select…</option>
                  {couriers.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select></div>
              <div><label className="text-xs font-semibold text-slate-500">Zone</label>
                <select data-testid="rc-form-zone" className={`${inputCls} mt-1`} value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}>
                  {ZONES.map((z) => <option key={z}>{z}</option>)}
                </select></div>
              {[["base_rate", "Base rate ₹ (0.5kg)"], ["additional_rate", "Additional ₹ / 0.5kg"], ["cod_fee", "COD fee ₹"], ["cod_pct", "COD %"], ["fuel_surcharge_pct", "Fuel surcharge %"], ["other_charges", "Other charges ₹"]].map(([k, l]) => (
                <div key={k}><label className="text-xs font-semibold text-slate-500">{l}</label>
                  <input data-testid={`rc-form-${k}`} type="number" step="0.01" required className={`${inputCls} mono mt-1`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
              ))}
            </div>
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-700">Saving creates a new version and archives the old one. Existing shipment prices are never affected.</p>
            <button data-testid="rc-save-btn" className="mt-4 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Save Version</button>
          </form>
        </div>
      )}
    </div>
  );
}
